import { apiClient } from '../client';
import type { Role } from '@never2busy/shared';
import type { Paginated, PaginationQuery } from './types';

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  shortName: string | null;
  phone: string | null;
  role: Role;
  canViewReports: boolean;
  active: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  shortName?: string;
  phone?: string;
  role: Role;
  canViewReports?: boolean;
}

export type UpdateUserInput = Partial<Omit<CreateUserInput, 'password'>> & { active?: boolean };

export interface UpdateOwnProfileInput {
  firstName?: string;
  lastName?: string;
  shortName?: string;
  phone?: string;
}

export interface ActiveUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

export const usersApi = {
  async list(q: PaginationQuery): Promise<Paginated<User>> {
    const { data } = await apiClient.get<Paginated<User>>('/users', { params: q });
    return data;
  },
  async listActive(): Promise<ActiveUser[]> {
    const { data } = await apiClient.get<ActiveUser[]>('/users/active');
    return data;
  },
  async findOne(id: number): Promise<User> {
    const { data } = await apiClient.get<User>(`/users/${id}`);
    return data;
  },
  async create(input: CreateUserInput): Promise<User> {
    const { data } = await apiClient.post<User>('/users', input);
    return data;
  },
  async update(id: number, input: UpdateUserInput): Promise<User> {
    const { data } = await apiClient.patch<User>(`/users/${id}`, input);
    return data;
  },
  async remove(id: number): Promise<void> {
    await apiClient.delete(`/users/${id}`);
  },
  async myProfile(): Promise<User> {
    const { data } = await apiClient.get<User>('/users/me/profile');
    return data;
  },
  async updateOwn(input: UpdateOwnProfileInput): Promise<User> {
    const { data } = await apiClient.patch<User>('/users/me/profile', input);
    return data;
  },
};
